import { useEffect, useRef, useState } from "react"
import api from "../../api"
import { Each } from "../../common/Each"
import typo from "../../typography.module.css"
import { formatSecondsDuration } from "../../utils"
import Button from "../Button"
import Card from "./Card"
import styles from "./OnlineCard.module.css"
import UserPlaceholder from "../../assets/images/placeholders/user-placeholder.png"

const OnlineCard = ({ modern = false }) => {

    const [total, setTotal] = useState(0)

    const [users, setUsers] = useState([])

    const [teachers, setTeachers] = useState([])
    const [admins, setAdmins] = useState([])
    const intervalRef = useRef(null);
    const updateIntervalRef = useRef(null);

    const [loading, setLoading] = useState(true)

    const getOnline = async () => {
        setLoading(true)
        try {
            const online = await api.get('/admin/online')
            setTotal(online.total)
            setUsers(online.users)
            setTeachers(online.teachers)
            setAdmins(online.admins)
        } catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    useEffect(() => {

        getOnline()

        updateIntervalRef.current = setInterval(async () => {
            await getOnline()
        }, 60000)

        intervalRef.current = setInterval(() => {
            setUsers((prev) => {
                return prev.map((user) => {
                    return {
                        ...user,
                        time: user.time + 1
                    }
                })
            })
            setTeachers((prev) => {
                return prev.map((user) => {
                    return {
                        ...user,
                        time: user.time + 1
                    }
                })
            })
            setAdmins((prev) => {
                return prev.map((user) => {
                    return {
                        ...user,
                        time: user.time + 1
                    }
                })
            })
        }, 1000)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
            clearInterval(updateIntervalRef.current)
        }
    }, [])

    return (
        <Card
        appearance={modern ? "modern" : undefined}
        actions={<Button appearance="subtle" onClick={getOnline} disabled={loading}>Aggiorna</Button>}
        style={{ display: modern ? 'block' : 'flex', minHeight: modern ? 0 : undefined, flex: modern ? '0 0 auto' : 1, height: modern ? 'auto' : '100%', position: 'relative' }}
        title={
            <span className={styles.title}><span className={styles.dot} />Utenti online <span className={styles.total}>{total}</span></span>
        }>
            {!modern && <Button appearance="subtle" style={{ position: 'absolute', top: '1rem', right: '1rem' }} onClick={getOnline} disabled={loading} >
                AGGIORNA
            </Button>}
            {modern && <div className={styles.modernOnline} aria-busy={loading}>
                <p className={styles.updateHint}>{loading ? 'Aggiornamento in corso…' : 'Attività in tempo reale · aggiornamento ogni minuto'}</p>
                <div className={styles.groups}>
                    {[{ label: 'Studenti', people: users }, { label: 'Docenti e tutor', people: teachers }, { label: 'Amministratori', people: admins }].map(group =>
                        <section className={styles.group} key={group.label}>
                            <h3>{group.label}<span>{group.people.length}</span></h3>
                            <div className={styles.people}>
                                {group.people.map((person, index) => <div className={styles.person} key={person.id ?? index}>
                                    <img src={person.picture || UserPlaceholder} alt="" />
                                    <div className={styles.personInfo}><strong>{person.name} {person.surname}</strong><span title={person.title}>{person.title || 'Pagina non disponibile'}</span></div>
                                    <time className={styles.duration} aria-label={`Tempo online: ${formatSecondsDuration(person.time)}`}>{formatSecondsDuration(person.time)}</time>
                                </div>)}
                                {!group.people.length && <p className={styles.empty}>{loading ? 'Caricamento…' : 'Nessun utente online'}</p>}
                            </div>
                        </section>
                    )}
                </div>
            </div>}
            {!modern && <div className={styles.onlineCard}>
                {modern && !loading && total === 0 && <p>Nessun utente online al momento.</p>}
                {(users.length > 0 || teachers.length > 0 || admins.length > 0) &&
                    < div className={styles.online}>
                        {users && users.length > 0 &&
                            <div className={styles.onlineList}>
                                <div className={`${typo.headline} ${styles.onlineListHeader}`}>Studenti • {users.length}</div>
                                <div className={styles.innerList}>
                                    <Each of={users} render={(user) => (
                                        <div className={styles.user}>
                                            <img src={user.picture ?? UserPlaceholder} alt={user.name} className={styles.userPicture} />
                                            <div className={styles.userInfo}>
                                                <div className={styles.userName}>{user.name} {user.surname}</div>
                                                <div className={styles.userPage}>{user.title}</div>
                                            </div>
                                            <div className={styles.spacer}></div>
                                            <div className={styles.userTime}>{formatSecondsDuration(user.time)}</div>
                                        </div>
                                    )} />
                                </div>
                            </div>
                        }
                        {teachers && teachers.length > 0 &&
                            <div className={styles.onlineList}>
                                <div className={`${typo.headline} ${styles.onlineListHeader}`}>Docenti e Tutor • {teachers.length}</div>
                                <div className={styles.innerList}>
                                    <Each of={teachers} render={(user) => (
                                        <div className={styles.user}>
                                            <img src={user.picture ?? UserPlaceholder} alt={user.name} className={styles.userPicture} style={{ border: '1.5px solid var(--secondary)' }} />
                                            <div className={styles.userInfo}>
                                                <div className={styles.userName}>{user.name} {user.surname}</div>
                                                <div className={styles.userPage}>{user.title}</div>
                                            </div>
                                            <div className={styles.spacer}></div>
                                            <div className={styles.userTime}>{formatSecondsDuration(user.time)}</div>
                                        </div>
                                    )} />
                                </div>
                            </div>
                        }
                        {
                            admins && admins.length > 0 &&
                            <div className={styles.onlineList}>
                                <div className={`${typo.headline} ${styles.onlineListHeader}`}>Amministratori • {admins.length}</div>
                                <div className={styles.innerList}>
                                    <Each of={admins} render={(user) => (
                                        <div className={styles.user}>
                                            <img src={user.picture ?? UserPlaceholder} alt={user.name} className={styles.userPicture} style={{ border: '1.5px solid var(--sf-red)' }} />
                                            <div className={styles.userInfo}>
                                                <div className={styles.userName}>{user.name} {user.surname}</div>
                                                <div className={styles.userPage}>{user.title}</div>
                                            </div>
                                            <div className={styles.spacer}></div>
                                            <div className={styles.userTime}>{formatSecondsDuration(user.time)}</div>
                                        </div>
                                    )} />
                                </div>
                            </div>
                        }
                    </div>

                }
            </div>}

        </Card >
    )

}

export default OnlineCard
